import throttle from 'lodash/throttle';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classnames from 'classnames';
import Textfield from 'textfield-component';
import AutoSuggestList from './AutoSuggestList';
import Button from 'button-component';

import {ANY_CHILD} from 'prop-utils';

class AutoSuggestInput extends Component {
    constructor(props) {
        super(props);

        this._onInputChange = this._onInputChange.bind(this);
        this._onInputFocus = this._onInputFocus.bind(this);
        this._onInputBlur = this._onInputBlur.bind(this);
        this._onSelectSuggest = this._onSelectSuggest.bind(this);
        this._selectSuggestion = this._selectSuggestion.bind(this);
        this._hideSuggests = this._hideSuggests.bind(this);
        this._onNextSuggest = this._onNextSuggest.bind(this);
        this._onPreviousSuggest = this._onPreviousSuggest.bind(this);
        this._onSuggestionMouseDown = this._onSuggestionMouseDown.bind(this);
        this._onSuggestionMouseOut = this._onSuggestionMouseOut.bind(this);
        this._throttledSearchSuggestions = throttle(() => {
            this._searchSuggestions();
        }, 500);

        this.state = {
            suggestionsHidden: true,
            ignoreBlur: false,
            value: props.initialValue,
            activeSuggestion: null,
            selectedSuggestion: null,
            suggestions: [],
            isArrowPress: false
        };
        this.clear = this.clear.bind(this);

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.initialValue !== this.props.initialValue) {
            this.setState({
                value: nextProps.initialValue,
                isArrowPress: false
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange
        } = this.props;
        const {
            value,
            suggestionsHidden
        } = this.state;

        if (prevState.value !== value) {
            if (!suggestionsHidden) {
                this._showSuggests();
            }

            if (!value) {
                this.focus();
            }

            if (onChange) {
                onChange(value);
            }
        }
    }

    _onInputChange(value) {
        this.setState({
            value,
            isArrowPress: false,
            selectedSuggestion: null
        });
    }

    _onInputFocus() {
        const {
            onFocus
        } = this.props;

        if (onFocus) {
            onFocus();
        }

        this._showSuggests();
    }

    _onInputBlur() {
        if (!this.state.ignoreBlur) {
            this._hideSuggests();
        }
    }

    _onSuggestionMouseDown() {
        this.setState({
            ignoreBlur: true
        });
    }

    _onSuggestionMouseOut() {
        this.setState({
            ignoreBlur: false
        });
    }

    _searchSuggestions() {
        const {
            value,
            isArrowPress
        } = this.state;

        if (isArrowPress) { return; }

        const {
            autoActivateFirstSuggest,
            searchSuggestions
        } = this.props;

        if (!value) {
            this._updateSuggests();
            return;
        } else {
            if (value.length < 2) {
                this._updateSuggests();
                return;
            }
        }

        searchSuggestions(value, suggestions => {
            this._updateSuggests(suggestions || []);

            if (autoActivateFirstSuggest) {
                this._activateSuggest({direction: 'next'});
            }
        });
    }

    _updateSuggests(suggestions = []) {
        const {
            getSuggestionLabel,
            fixtures,
            isVehicle
        } = this.props;
        const {
            value
        } = this.state;
        const newSuggestions = [];

        fixtures.forEach(suggestion => {
            if (!value) {
                newSuggestions.push(suggestion);
            }
        });

        if (isVehicle) {
            suggestions.forEach((suggestion, i) => {
                newSuggestions.push({
                    label: suggestion.label,
                    unlistedModel: suggestion.unlisted
                });

                if (suggestion.id) {
                    newSuggestions[i].id = suggestion.id;
                }
            });
        } else {
            suggestions.forEach(suggestion => {
                newSuggestions.push({
                    label: getSuggestionLabel(suggestion),
                    placeId: suggestion.place_id,
                    placesPlaceId: suggestion.place_id,
                    reference: suggestion.reference
                });
            });
        }

        this.setState({
            suggestions: newSuggestions
        });
    }

    _showSuggests() {
        this._throttledSearchSuggestions();

        this.setState({
            suggestionsHidden: false
        });
    }

    _hideSuggests() {
        const {
            onBlur
        } = this.props;

        if (onBlur) {
            onBlur();
        }

        this.setState({
            suggestionsHidden: true
        });
    }

    _onNextSuggest() {
        this._activateSuggest({
            direction: 'next',
            isArrowPress: true
        });
    }

    _onPreviousSuggest() {
        this._activateSuggest({
            direction: 'prev',
            isArrowPress: true
        });
    }

    _activateSuggest({direction, isArrowPress = false}) {
        const {
            suggestionsHidden,
            suggestions,
            activeSuggestion
        } = this.state;

        if (suggestionsHidden) {
            this._showSuggests();
            return;
        }

        const suggestionsCount = suggestions.length - 1;
        const next = direction === 'next';
        const newState = {};
        let newActiveSuggestion = null;
        let newIndex = 0;
        let i = 0;

        for (i; i <= suggestionsCount; i++) {
            if (suggestions[i] === activeSuggestion) {
                newIndex = next ? i + 1 : i - 1;
            }
        }

        if (!activeSuggestion) {
            newIndex = next ? 0 : suggestionsCount;
        }

        if (newIndex < 0) {
            newIndex = suggestionsCount;
        } else if (newIndex > suggestionsCount) {
            newIndex = 0;
        }

        if (newIndex >= 0 && newIndex <= suggestionsCount) {
            newActiveSuggestion = suggestions[newIndex];
        }

        newState.activeSuggestion = newActiveSuggestion;

        if (isArrowPress) {
            newState.isArrowPress = true;
        }

        this.setState(newState);
    }

    _onSelectSuggest(evt) {
        evt.preventDefault();

        this._selectSuggestion({
            suggestion: this.state.activeSuggestion
        });
    }

    _selectSuggestion({suggestion = false}) {
        if (!suggestion) {
            suggestion = {
                label: this.state.value
            };
        }

        this.setState({
            suggestionsHidden: true,
            value: suggestion.label,
            isArrowPress: false,
            activeSuggestion: null,
            selectedSuggestion: suggestion
        });

        this.props.onSuggestionSelect(suggestion);
    }

    focus() {
        this._input.focus();
    }

    clear() {
        const {
            onClear
        } = this.props;

        this.setState({
            value: '',
            isArrowPress: false,
            activeSuggestion: null
        });

        if (onClear) {
            onClear();
        }
    }

    _generateDataTestId(prefix, purpose) {
        if (typeof purpose === 'undefined') {
            return `${prefix}-default`;
        } else {
            return `${prefix}-${purpose}`;
        }
    }

    render() {
        const {
            className,
            size,
            name,
            icon,
            placeholder,
            showPoweredByGoogle,
            label,
            required,
            formId,
            error,
            validateOn,
            forceShowError,
            formBinding,
            isVehicle,
            dataTestIdPurposeForInput,
            dataTestIdPurposeForButton,
            dataTestIdPurposeForList,
            dataTestIdPurposeForItem
        } = this.props;
        const {
            value,
            activeSuggestion,
            suggestionsHidden,
            suggestions
        } = this.state;
        const classes = classnames(
            'AutoSuggestInput',
            {'AutoSuggestInput-suggestions-active': !suggestionsHidden},
            {'AutoSuggestInput-no-suggestions': !suggestions.length},
            {'AutoSuggestInput-is-vehicle': isVehicle},
            className
        );

        return (
            <div className={classes}>
                <Textfield
                    type="text"
                    ref={node => { this._input = node; }}
                    size={size}
                    icon={icon}
                    name={name}
                    value={value}
                    label={label}
                    required={required}
                    placeholder={placeholder}
                    formId={formId}
                    formBinding={formBinding}
                    error={error}
                    validateOn={validateOn}
                    forceShowError={forceShowError}
                    dataTestId={this._generateDataTestId('AutoSuggestInput-textfield', dataTestIdPurposeForInput)}
                    onChange={this._onInputChange}
                    onFocus={this._onInputFocus}
                    onBlur={this._onInputBlur}
                    onDownPress={this._onNextSuggest}
                    onUpPress={this._onPreviousSuggest}
                    onEnterPress={this._onSelectSuggest}
                    onEscapePress={this._hideSuggests}
                    disableAutocomplete
                />
                <AutoSuggestList
                    hidden={suggestionsHidden}
                    suggestions={suggestions}
                    activeSuggestion={activeSuggestion}
                    onSuggestionSelect={this._selectSuggestion}
                    onSuggestionMouseDown={this._onSuggestionMouseDown}
                    onSuggestionMouseOut={this._onSuggestionMouseOut}
                    showPoweredByGoogle={showPoweredByGoogle}
                    dataTestIdPurposeForList={dataTestIdPurposeForList}
                    dataTestIdPurposeForItem={dataTestIdPurposeForItem}
                />
                {value &&
                    <Button
                        onClick={this.clear}
                        className="AutoSuggestInput-clear"
                        icon="times-circle"
                        dataTestId={this._generateDataTestId('AutoSuggestInput-button', dataTestIdPurposeForButton)}
                    />
                }
                {isVehicle &&
                    <span className="example-text">Example: Honda Civic</span>
                }
            </div>
        );
    }
}

AutoSuggestInput.propTypes = {
    fixtures: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            className: PropTypes.string,
            label: PropTypes.string,
            placeId: PropTypes.string,
            placesPlaceId: PropTypes.string,
            reference: PropTypes.string,
            location: PropTypes.shape({
                lat: PropTypes.string,
                lng: PropTypes.string
            }),
            /** If a url is provided it will override the default click action and go to the url instead. */
            url: PropTypes.string
        })
    ),
    initialValue: PropTypes.string,
    isVehicle: PropTypes.bool,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(['lg', 'md']),
    label: ANY_CHILD,
    required: PropTypes.bool,
    name: PropTypes.string,
    icon: PropTypes.string,
    autoActivateFirstSuggest: PropTypes.bool,
    searchSuggestions: PropTypes.func.isRequired,
    onSuggestionSelect: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    getSuggestionLabel: PropTypes.func,
    showPoweredByGoogle: PropTypes.bool,
    formId: PropTypes.string,
    formBinding: PropTypes.func,
    error: PropTypes.string,
    validateOn: PropTypes.oneOf(['blur', 'change', 'submit']),
    forceShowError: PropTypes.bool,
    dataTestIdPurposeForInput: PropTypes.string,
    dataTestIdPurposeForButton: PropTypes.string,
    dataTestIdPurposeForList: PropTypes.string,
    dataTestIdPurposeForItem: PropTypes.string
};

AutoSuggestInput.defaultProps = {
    fixtures: [],
    initialValue: '',
    name: 'search',
    icon: 'search',
    placeholder: 'Search',
    className: '',
    inputClassName: '',
    getSuggestionLabel: suggestion => suggestion.description
};

export default AutoSuggestInput;



// WEBPACK FOOTER //
// ./frontend/src/js/components/auto-suggest/AutoSuggestInput.jsx